import { Avatar, Chip, CircularProgress, makeStyles, Paper, Popper, PopperProps } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import {
  createContext,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getIcon, IconType } from '../modules/data';
import { Option } from '../modules/i18n-helpers';
import { getLabel } from '../modules/labels';

import AutocompleteItem from './AutocompleteItem';

const useStyles = makeStyles((theme) => ({
  checkbox: { marginRight: theme.spacing(1), padding: 0 },
  chip: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(0.25),
    '& .MuiChip-label': {
      marginLeft: '8px',
    },
  },
  iconImage: { display: 'inline-block', width: 20, height: 'auto', marginRight: theme.spacing(1) },
  notchedOutline: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  selectAll: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    width: '95%',
    justifyContent: 'left',
    paddingTop: theme.spacing(1),
  },
  heightReduce: {
    paddingTop: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  small: {
    minWidth: '8px !important',
  },
  noLabel: {
    '& .MuiChip-label': {
      display: 'none',
    },
    '& .MuiChip-avatarSmall': {
      marginRight: '6px',
    },
  },
  hidden: {
    opacity: 0.5,
  },
}));

type FilterAutocompleteProps = {
  name: string;
  defaultValue?: string | string[];
  label: string;
  iconType?: IconType;
  optionsWithoutIcon?: readonly string[];
  IconComponent?: ({ value }: { value: string }) => ReactElement;
  disableClearable?: boolean;
  multiple?: boolean;
  options: Readonly<Option[]>;
  isLoading?: boolean;
  highlightTextField?: boolean;
  onChipDelete?: (value: string) => void;
  selectAllOnDefault?: boolean;
  maxOptions?: boolean;
  groupBy?: (option: string) => string;
  customOnChange?: (value: string | string[] | null) => void;
  hideCheckbox?: boolean;
  className?: string;
  shownTags?: number;
  noLabel?: boolean;
  smallInput?: boolean;
  historicOptions?: string[];
  exclusiveMode?: boolean;
};

const maxLoad = 50;

type AutocompletePopperContextValue = {
  name: string;
  optionCount: number;
  onSelectAll: (event: MouseEvent) => void;
  onLoadMore: (event: MouseEvent) => void;
  showLoadMore: boolean;
  exclusiveMode: boolean;
};

const AutocompletePopperContext = createContext<AutocompletePopperContextValue | null>(null);

// Defined at module level so MUI receives a stable PopperComponent. An inline definition is a new
// component type on every render, which remounts the open list and resets its scroll position.
const AutocompletePopper = ({ children, ...rest }: PopperProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const popperContext = useContext(AutocompletePopperContext);
  const selectedCount = popperContext ? getValues(popperContext.name)?.length : undefined;
  const optionCount = popperContext?.optionCount;

  return (
    <Popper {...rest}>
      <Paper>
        <div
          role="button"
          tabIndex={0}
          className={clsx('MuiPaper-root', 'MuiAutocomplete-paper', 'MuiPaper-elevation1')}
          onMouseDown={popperContext?.onSelectAll}
        >
          <li className="MuiAutocomplete-option">
            <AutocompleteItem
              value={t('common.all')}
              label={t('common.all')}
              checked={selectedCount === optionCount}
              indeterminate={selectedCount !== optionCount && selectedCount !== 0}
              exclusiveMode={popperContext?.exclusiveMode}
            />
          </li>
        </div>
        {children as ReactNode}
        {popperContext?.showLoadMore && (
          <div
            role="button"
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            tabIndex={1}
            className={clsx('MuiPaper-root', 'MuiAutocomplete-paper', 'MuiPaper-elevation1')}
            onMouseDown={popperContext.onLoadMore}
          >
            <li className="MuiAutocomplete-option">Load more...</li>
          </div>
        )}
      </Paper>
    </Popper>
  );
};

const FilterAutocomplete = ({
  name,
  defaultValue,
  label,
  iconType,
  optionsWithoutIcon = [],
  IconComponent,
  disableClearable = false,
  multiple = false,
  options,
  isLoading = false,
  highlightTextField,
  onChipDelete,
  maxOptions,
  groupBy,
  customOnChange,
  hideCheckbox = false,
  className,
  shownTags = 5,
  noLabel = false,
  smallInput = false,
  historicOptions = [],
  exclusiveMode = false,
}: FilterAutocompleteProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [maxShown, setMaxShown] = useState(maxLoad);
  const optionValues = options.map(({ value }) => value);
  const slicedOptions = options.map(({ value }) => value).slice(0, maxShown);

  const [inputValue, setInputValue] = useState('');

  const suggestionsRef = useRef(new Set<string>());
  const onChangeRef = useRef<(value: string[]) => void>();
  const { getValues } = useFormContext();

  const optionCount = optionValues.length;

  const handleSelectAll = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      let newInput: string[];
      const suggArr = Array.from(suggestionsRef.current);
      switch (getValues(name)?.length) {
        case 0:
          newInput = suggArr;
          break;
        default:
          newInput = [];
          break;
      }
      onChangeRef.current?.(newInput);
      if (customOnChange) customOnChange(newInput);
    },
    [getValues, name, customOnChange],
  );

  const handleLoadMore = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      setMaxShown((shown) => (shown < optionCount ? shown + maxLoad : shown));
    },
    [optionCount],
  );

  const showLoadMore = !!maxOptions && maxShown < optionCount;
  const popperContext = useMemo<AutocompletePopperContextValue>(
    () => ({
      name,
      optionCount,
      onSelectAll: handleSelectAll,
      onLoadMore: handleLoadMore,
      showLoadMore,
      exclusiveMode,
    }),
    [name, optionCount, handleSelectAll, handleLoadMore, showLoadMore, exclusiveMode],
  );

  return (
    <AutocompletePopperContext.Provider value={popperContext}>
      <Controller
        name={name}
        defaultValue={defaultValue}
        render={({ field: { onChange, ...field } }): ReactElement => {
          onChangeRef.current = onChange;
          return (
            <Autocomplete
              {...field}
              {...(multiple && {
                inputValue,
                onBlur: () => setInputValue(''),
                onKeyDown: (event) => {
                  const suggArr = Array.from(suggestionsRef.current);
                  switch (event.key) {
                    case 'a': // STRG + A should select all
                      if (event.ctrlKey || event.metaKey) {
                        onChange(suggArr);
                        if (customOnChange) customOnChange(suggArr);
                      }
                      break;
                    default:
                      break;
                  }
                },
                PopperComponent: AutocompletePopper,
              })}
              onChange={(_, value) => {
                if (customOnChange) {
                  customOnChange(value);
                }
                onChange(value);
              }}
              onInputChange={() => {
                suggestionsRef.current = new Set();
              }}
              disableClearable={disableClearable}
              multiple={multiple}
              options={optionValues}
              limitTags={shownTags}
              className={className}
              disableCloseOnSelect={multiple}
              getOptionLabel={(option: string) => getLabel(options, option)}
              noOptionsText={t('carsTable.noEntriesFound')}
              groupBy={groupBy}
              renderOption={(value, { selected }) => {
                suggestionsRef.current.add(value);
                if (!maxOptions)
                  return (
                    <AutocompleteItem
                      hideCheckbox={hideCheckbox}
                      checked={selected}
                      iconType={iconType}
                      hideIcon={optionsWithoutIcon.includes(value)}
                      value={value}
                      alt={label}
                      IconComponent={IconComponent}
                      label={getLabel(options, value)}
                      historicOption={historicOptions.includes(value)}
                      exclusiveMode={exclusiveMode}
                    />
                  );
                if (
                  slicedOptions.includes(value) ||
                  (suggestionsRef.current.size !== optionValues.length && suggestionsRef.current.has(value))
                ) {
                  return (
                    <AutocompleteItem
                      hideCheckbox={hideCheckbox}
                      checked={selected}
                      iconType={iconType}
                      hideIcon={optionsWithoutIcon.includes(value)}
                      value={value}
                      alt={label}
                      IconComponent={IconComponent}
                      label={getLabel(options, value)}
                      historicOption={historicOptions.includes(value)}
                      exclusiveMode={exclusiveMode}
                    />
                  );
                }

                return null;
              }}
              renderInput={(params): ReactElement => (
                <TextField
                  {...params}
                  {...(multiple && {
                    onChange: (event) => {
                      setInputValue(event.currentTarget.value);
                    },
                  })}
                  variant="outlined"
                  label={label}
                  margin="dense"
                  className={classes.heightReduce}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      notchedOutline: highlightTextField ? classes.notchedOutline : undefined,
                      input: smallInput ? classes.small : undefined,
                    },
                    endAdornment: (
                      <>
                        {isLoading && <CircularProgress color="secondary" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(tagValues, getTagProps) =>
                tagValues.map((option, index) => {
                  const tagProps: any = getTagProps({ index });
                  return (
                    /* eslint-disable react/jsx-props-no-spreading */
                    <Chip
                      {...tagProps}
                      size="small"
                      className={clsx(
                        classes.chip,
                        noLabel && classes.noLabel,
                        !optionValues.includes(option) && classes.hidden,
                      )} //
                      onDelete={onChipDelete ? () => onChipDelete(option) : tagProps.onDelete}
                      label={getLabel(options, option)}
                      avatar={
                        iconType && !optionsWithoutIcon.includes(option) ? (
                          <Avatar src={getIcon(iconType, option)} imgProps={{ style: { transform: 'scale(1.1)' } }} />
                        ) : undefined
                      }
                    />
                  );
                })
              }
            />
          );
        }}
      />
    </AutocompletePopperContext.Provider>
  );
};

export default FilterAutocomplete;
